// ════════════════════════════════════════════════════════════════════════
//  Inhaltsschema für ein Lehrgangs-Bundle
//
//  Das ist die EINE Wahrheitsquelle: die Lern-App prüft damit ein geladenes
//  Bundle, die Admin-App prüft damit einen Entwurf vor dem Veröffentlichen.
//  Zwei Prüfungen würden auseinanderlaufen – es gibt deshalb nur diese.
//
//  Bezeichner sind englisch, damit die Zuordnung zu den SQL-Spalten
//  (lesson_key, stage_key) sichtbar bleibt; Inhalte und Kommentare deutsch.
// ════════════════════════════════════════════════════════════════════════

import { z } from "zod";

/** Stabile Schlüssel: einmal vergeben, nie wiederverwendet, nie umbenannt. */
const keySchema = z
  .string()
  .min(1)
  .max(64)
  .regex(
    /^[a-z0-9]+(?:[-.][a-z0-9]+)*$/,
    "Schlüssel: nur Kleinbuchstaben, Ziffern, - und . als Trenner",
  );

const colorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Farbe als #RRGGBB");

/**
 * Die Finger. Eine Konstante, kein Datum – es werden weder mehr noch weniger,
 * und deshalb steht die Liste im Code und nicht im Bundle.
 *
 * NEUN Einträge für zehn Finger: die beiden Daumen teilen sich einen. Für diesen
 * Lehrgang spielt es keine Rolle, welcher die Leertaste drückt, und das Heft
 * sagt genau so neutral „dafür ist der Daumen da“. Zwei Einträge wären eine
 * Behauptung, die niemand aufgestellt hat.
 *
 * Reihenfolge wie an der Tastatur, von links nach rechts: so liest sich die
 * Tabelle in der Admin-App wie die Hände darüber.
 */
export const FINGER_IDS = [
  "links-klein",
  "links-ring",
  "links-mittel",
  "links-zeige",
  "daumen",
  "rechts-zeige",
  "rechts-mittel",
  "rechts-ring",
  "rechts-klein",
] as const;

export type FingerId = (typeof FINGER_IDS)[number];

/**
 * Ein Finger mit seiner Farbe und den Tasten, für die er zuständig ist.
 *
 * Umgekehrt herum als früher: da stand je TASTE ein Eintrag mit Fingernamen und
 * Farbe, also 23 Zeilen für zwölf Namen – und mit dem Ausbau aufs ganze
 * Tastaturfeld wären es neunzig geworden. „linker Zeigefinger“ stand viermal
 * da, die Farbe jedes Mal abgeschrieben; nichts hinderte daran, `f` gelb und `F`
 * grün zu machen. Jetzt gehört die Farbe dem Finger, und das lässt sich gar
 * nicht mehr ausdrücken.
 *
 * Was die Tasten sind, bleibt Inhalt: der Ausbau aufs ganze Tastaturfeld ändert
 * die Listen, nicht den Code.
 */
export const fingerSchema = z.object({
  id: z.enum(FINGER_IDS),
  name: z.string().min(1),
  color: colorSchema,
  /** Alle Zeichen dieses Fingers – Groß und Klein getrennt, " " ist die Leertaste. */
  keys: z.array(z.string().length(1)),
});

/**
 * Die Reihen der Tastatur. Wie die Finger eine Konstante: welche Reihen eine
 * Tastatur hat, entscheidet nicht die Redaktion.
 *
 * Sie haben Namen und keine Nummern, weil an ihnen etwas hängt: die Lern-App
 * setzt die Feststelltaste an die Grundreihe und die Umschalttasten an die
 * untere. Das stand als `i === 1` und `i === 2` im Code – eine Ziffernreihe
 * davor hätte beides stillschweigend um eins verschoben, ohne Absturz und ohne
 * Test, nur mit einer falsch abgemalten Tastatur.
 */
export const ROW_IDS = ["zahlen", "oben", "grund", "unten", "leer"] as const;

export type RowId = (typeof ROW_IDS)[number];

export const keyRowSchema = z.object({
  id: z.enum(ROW_IDS),
  keys: z.array(z.string().length(1)).min(1),
});

export const stageSchema = z.object({
  key: keySchema,
  title: z.string().min(1),
  hint: z.string().min(1),
  /** Übungstext. Pädagogisch abgestimmt – nie ohne die Lehrkraft ändern. */
  text: z.string().min(1).max(200),
});

/**
 * Der Eingangs- und Abschlusstest eines Lehrgangs.
 *
 * Dasselbe Stück Text, einmal VOR dem Lehrgang getippt und einmal danach. Was
 * dabei herauskommt, sind Wörter pro Minute – die Zahl, mit der sich die
 * Wirksamkeit des Lehrgangs belegen lässt.
 *
 * Das Feld ist OPTIONAL, und das ist der Schalter: fehlt es, arbeitet dieser
 * Lehrgang ohne Test. Ein zusätzliches `enabled: false` liesse einen Text
 * stehen, den niemand sieht, und irgendwann weiss keiner mehr, ob er gilt.
 *
 * Der Test ist KEINE Stufe. Stünde er in `lessons`, zählte er beim Abschluss
 * des Lehrgangs mit, tauchte in der Ergebnisübersicht als Übung auf und bekäme
 * Sterne – vier Nebenwirkungen für ein gespartes Feld.
 */
export const assessmentSchema = z.object({
  key: keySchema,
  title: z.string().min(1),
  /** Was das Kind vor dem Tippen liest. */
  intro: z.string().min(1),
  /**
   * Der Testtext. Kurz genug, dass ein Kind vor dem Lehrgang nicht aufgibt –
   * die Prüfung mahnt ab etwa 120 Zeichen.
   */
  text: z.string().min(20).max(200),
});

export const lessonSchema = z.object({
  key: keySchema,
  /** Anzeigenummer. Reihenfolge steckt in `order`, nicht im Array-Index. */
  number: z.number().int().positive(),
  order: z.number().int().nonnegative(),
  title: z.string().min(1),
  subtitle: z.string().min(1),
  color: colorSchema,
  /** Tasten, die diese Lektion einführt bzw. übt (Tastaturbild hebt sie hervor). */
  keys: z.array(z.string().length(1)).min(1),
  intro: z.string().min(1),
  /**
   * Auf welchem Weg die Großbuchstaben dieser Lektion entstehen sollen. Fehlt das
   * Feld, kümmert die Lektion es nicht – so ist es in der ganzen Grundreihe.
   *
   * Kapitel 5 des Hefts behandelt beide Wege getrennt: 5a die Umschalttaste, 5b
   * die Feststelltaste. Dieselbe Zeile, zwei Wege, und nur einer ist die Aufgabe.
   * Unterschieden wird am Anschlag über `shiftKey` – das meldet jeder Browser.
   */
  uppercaseWith: z.enum(["shift", "capsLock"]).optional(),
  stages: z.array(stageSchema).min(1),
});

export const bundleSchema = z.object({
  /** Formatversion des Schemas, nicht die Fassung des Lehrgangs. */
  schemaVersion: z.literal(1),
  slug: keySchema,
  title: z.string().min(1),
  /** Sterne-Schwellen. Müssen mit student_save_progress im SQL übereinstimmen. */
  starThresholds: z.object({
    three: z.number().min(0).max(1),
    two: z.number().min(0).max(1),
  }),
  keyboard: z.object({
    /** Tastaturbild, Reihe für Reihe – genau die aus `ROW_IDS`. */
    rows: z.array(keyRowSchema).length(ROW_IDS.length),
    /** Grundstellung: Taste → Farbe, für die Fingerpunkte unter den Tasten. */
    homeRow: z.array(z.object({ key: z.string().length(1), color: colorSchema })).min(1),
  }),
  /** Genau so viele wie `FINGER_IDS` – die Vollständigkeit prüft `checkBundle`. */
  fingers: z.array(fingerSchema).length(FINGER_IDS.length),
  /**
   * Der Test dieses Lehrgangs, oder gar keiner. Siehe `assessmentSchema`.
   */
  assessment: assessmentSchema.optional(),
  /**
   * Mindestens eine Lektion verlangt NICHT das Schema, sondern `checkBundle`.
   *
   * Der Unterschied ist der zwischen „kaputt“ und „noch nicht fertig“. Ein
   * frisch angelegter Lehrgang hat null Lektionen, und die Admin-App muss ihn
   * laden können, um welche anzulegen. Stand es im Schema, scheiterte schon das
   * Laden – und die Arbeitsfläche zeigte einen Lehrgang, an dem sich nichts tun
   * ließ, weil nichts an ihm getan worden war.
   *
   * Für die Lern-App ändert sich dadurch nichts: `parseBundle` prüft beides, und
   * eine veröffentlichte Fassung ohne Lektion fällt dort weiterhin durch.
   */
  lessons: z.array(lessonSchema),
});

export type Assessment = z.infer<typeof assessmentSchema>;
export type Finger = z.infer<typeof fingerSchema>;
export type KeyRow = z.infer<typeof keyRowSchema>;

/** Eine Reihe des Tastaturbilds – benannt, nicht gezählt. */
export function keyRow(bundle: Bundle, id: RowId): KeyRow | null {
  return bundle.keyboard.rows.find((r) => r.id === id) ?? null;
}

/**
 * Taste → Finger. Der Weg, den fast jeder Aufrufer braucht: das Bundle listet
 * je Finger seine Tasten, gefragt wird immer nach einer Taste.
 *
 * Gemerkt je Bundle, weil das Tastaturbild die Funktion für jede der dreißig
 * Tasten einmal ruft – und das bei jedem Anschlag neu.
 */
const indexCache = new WeakMap<Bundle, Map<string, Finger>>();

export function fingerIndex(bundle: Bundle): Map<string, Finger> {
  const known = indexCache.get(bundle);
  if (known) return known;

  const index = new Map<string, Finger>();
  for (const finger of bundle.fingers) {
    for (const key of finger.keys) if (!index.has(key)) index.set(key, finger);
  }
  indexCache.set(bundle, index);
  return index;
}
export type Stage = z.infer<typeof stageSchema>;
export type Lesson = z.infer<typeof lessonSchema>;
export type Bundle = z.infer<typeof bundleSchema>;

// ────────────────────────────────────────────────────────────────────────
//  Prüfung
// ────────────────────────────────────────────────────────────────────────

export type Problem = { readonly path: string; readonly message: string };

/**
 * Prüft, was ein Zod-Schema nicht ausdrücken kann: Eindeutigkeit von Schlüsseln
 * und inhaltliche Vollständigkeit. Gibt eine Liste zurück statt zu werfen – die
 * Wer einen Entwurf prüft, soll alle Probleme auf einmal sehen, nicht eines pro Anlauf.
 */
export function checkBundle(bundle: Bundle): Problem[] {
  const problems: Problem[] = [];
  const fingerKeys = new Set(bundle.fingers.flatMap((f) => f.keys));

  // Ein Lehrgang ohne Lektion ist nichts, womit ein Kind üben könnte. Als
  // Hinweis und nicht als Formfehler: siehe `lessons` im Schema.
  if (bundle.lessons.length === 0) {
    problems.push({ path: "lessons", message: "Der Lehrgang hat noch keine Lektion" });
  }

  // ── Genau ein Zeichen je Taste ────────────────────────────────────────
  //
  // Das Zod-Schema verlangt das auch, aber es läuft nur beim LADEN einer
  // Fassung – vor dem Veröffentlichen zählt allein, was hier herauskommt.
  // Vorher hat das kein Mensch geprüft: die Eingabefelder der Admin-App warfen
  // zu lange Stücke einfach weg, und damit war der Eingabeschutz eine
  // Nebenwirkung des Tippens.
  const singleChar = (value: string, path: string, what: string) => {
    if ([...value].length !== 1) {
      problems.push({
        path,
        message: `${what} muss genau ein Zeichen sein, ist aber "${value}"`,
      });
      return false;
    }
    return true;
  };

  // ── Die Reihen ───────────────────────────────────────────────────────
  const seenRows = new Set<string>();
  for (const row of bundle.keyboard.rows) {
    if (seenRows.has(row.id)) {
      problems.push({
        path: "keyboard.rows",
        message: `Reihe "${row.id}" steht doppelt`,
      });
    }
    seenRows.add(row.id);
    for (const key of row.keys) {
      singleChar(key, "keyboard.rows", `Taste "${key}" in Reihe "${row.id}"`);
    }
  }
  for (const id of ROW_IDS) {
    if (!seenRows.has(id)) {
      problems.push({ path: "keyboard.rows", message: `Reihe "${id}" fehlt` });
    }
  }

  for (const spot of bundle.keyboard.homeRow) {
    if (!singleChar(spot.key, "keyboard.homeRow", `Taste "${spot.key}"`)) continue;
    // Ohne Finger bekäme der Punkt unter der Taste eine Ersatzfarbe – und ein
    // Kind lernte eine Farbzuordnung, die es sonst nirgends gibt.
    if (!fingerKeys.has(spot.key)) {
      problems.push({
        path: "keyboard.homeRow",
        message: `Grundstellungstaste "${spot.key}" hat keine Finger-Zuordnung`,
      });
    }
  }

  // ── Alle Finger, jede Taste bei genau einem ──────────────────────────
  //
  // Die Zahl prüft schon das Zod-Schema; hier geht es darum, dass es die
  // richtigen sind. Zweimal derselbe Finger und einer fehlt wäre sonst
  // eine gültige Liste – und eine Taste ohne Farbe die Folge.
  const seenFingers = new Set<string>();
  for (const finger of bundle.fingers) {
    if (seenFingers.has(finger.id)) {
      problems.push({ path: "fingers", message: `Finger "${finger.id}" steht doppelt` });
    }
    seenFingers.add(finger.id);
  }
  for (const id of FINGER_IDS) {
    if (!seenFingers.has(id)) {
      problems.push({ path: "fingers", message: `Finger "${id}" fehlt` });
    }
  }

  // Eine Taste gehört genau einem Finger. Zwei Ansprüche hießen: zwei Farben
  // für dieselbe Taste, und welche gewinnt, entschiede die Reihenfolge.
  const claimedBy = new Map<string, string>();
  bundle.fingers.forEach((finger, i) => {
    for (const key of finger.keys) {
      if (!singleChar(key, `fingers.${i}`, `Taste "${key}" bei "${finger.name}"`))
        continue;
      const other = claimedBy.get(key);
      if (other) {
        problems.push({
          path: `fingers.${i}`,
          message: `Taste "${key === " " ? "␣" : key}" ist "${other}" und "${finger.name}" zugeordnet`,
        });
      }
      claimedBy.set(key, finger.name);
    }
  });

  if (bundle.starThresholds.three <= bundle.starThresholds.two) {
    problems.push({
      path: "starThresholds",
      message: "Schwelle für drei Sterne muss über der für zwei liegen",
    });
  }

  const seenLessons = new Set<string>();
  const seenStages = new Set<string>();
  const seenOrder = new Set<number>();

  for (const lesson of bundle.lessons) {
    const at = `lessons.${lesson.key}`;
    if (seenLessons.has(lesson.key)) {
      problems.push({ path: at, message: "Lektionsschlüssel doppelt" });
    }
    seenLessons.add(lesson.key);

    if (seenOrder.has(lesson.order)) {
      problems.push({ path: at, message: `order ${lesson.order} doppelt` });
    }
    seenOrder.add(lesson.order);

    const lessonKeys = new Set(lesson.keys);
    for (const key of lesson.keys) {
      // Erst die Länge: bei "ax" ist nicht die Zuordnung das Problem, sondern
      // dass da zwei Zeichen stehen. Beide Meldungen zugleich verwirren nur.
      if (!singleChar(key, `${at}.keys`, `Taste "${key}"`)) continue;
      if (!fingerKeys.has(key)) {
        problems.push({
          path: `${at}.keys`,
          message: `Taste "${key}" hat keine Finger-Zuordnung`,
        });
      }
    }

    // Verlangt die Lektion einen Weg, muss ihr Text dazu passen. Beides sind
    // Redaktionsfehler, die im Klassenzimmer als Stufe auffallen, die sich nicht
    // tippen lässt.
    if (lesson.uppercaseWith !== undefined) {
      const chars = lesson.stages.flatMap((s) => [...s.text]);
      if (!chars.some((c) => c !== c.toLowerCase())) {
        problems.push({
          path: `${at}.uppercaseWith`,
          message: "Weg für Großbuchstaben gesetzt, aber kein Großbuchstabe im Text",
        });
      }
      // Nur bei der Feststelltaste: sie rastet, und ein kleiner Buchstabe
      // zwischendrin verlangte, sie mitten in der Zeile zu lösen.
      if (lesson.uppercaseWith === "capsLock") {
        const small = chars.find((c) => c === c.toLowerCase() && c !== c.toUpperCase());
        if (small) {
          problems.push({
            path: `${at}.uppercaseWith`,
            message: `Feststelltaste verlangt, aber "${small}" ist klein – mit gerasteter Taste nicht tippbar`,
          });
        }
      }
    }

    for (const stage of lesson.stages) {
      const stageAt = `${at}.${stage.key}`;
      if (seenStages.has(stage.key)) {
        problems.push({ path: stageAt, message: "Stufenschlüssel doppelt" });
      }
      seenStages.add(stage.key);

      // Der häufigste Redaktionsfehler – und im Klassenzimmer sofort sichtbar
      // als Taste, die sich nicht einfärbt.
      for (const char of new Set(stage.text)) {
        if (!fingerKeys.has(char)) {
          problems.push({
            path: `${stageAt}.text`,
            message: `Zeichen "${char}" hat keine Finger-Zuordnung`,
          });
        } else if (!lessonKeys.has(char)) {
          problems.push({
            path: `${stageAt}.text`,
            message: `Zeichen "${char}" steht nicht in den Tasten der Lektion`,
          });
        }
      }
    }
  }

  // ── Der Test ─────────────────────────────────────────────────────────
  if (bundle.assessment) {
    const test = bundle.assessment;
    const at = "assessment.text";

    if (seenLessons.has(test.key) || seenStages.has(test.key)) {
      problems.push({
        path: "assessment",
        message: "Testschlüssel wird schon von einer Lektion oder Stufe benutzt",
      });
    }

    for (const char of new Set(test.text)) {
      // Ein Grossbuchstabe ist im Eingangstest nicht schwer, sondern unlösbar:
      // die Engine verlangt ihn exakt (das ist der Inhalt von Kapitel 5), und
      // wer die Umschalttaste noch nicht kennt, beendet die Zeile nie.
      if (char !== char.toLowerCase()) {
        problems.push({
          path: at,
          message: `"${char}" ist gross – im Test hat noch niemand die Umschalttaste gelernt`,
        });
      } else if (!fingerKeys.has(char)) {
        problems.push({
          path: at,
          message: `Zeichen "${char}" hat keine Finger-Zuordnung`,
        });
      }
    }

    // Kein Fehler, sondern eine Mahnung: ein langer Text ist vor dem Lehrgang
    // eine Zumutung. Ein Kind, das noch sucht, tippt 25 Zeichen in der Minute.
    if ([...test.text].length > 120) {
      problems.push({
        path: at,
        message: `${[...test.text].length} Zeichen – vor dem Lehrgang dauert das sehr lange, 60 bis 120 sind genug`,
      });
    }
  }

  return problems;
}

/**
 * Prüft ein bereits veröffentlichtes gegen ein neues Bundle: kein Schlüssel darf
 * verschwinden. Sonst verwaisen die Fortschrittszeilen der Kinder, die mit der
 * alten Fassung geübt haben.
 */
export function checkKeysKept(published: Bundle, draft: Bundle): Problem[] {
  const draftLessons = new Set(draft.lessons.map((l) => l.key));
  const draftStages = new Set(draft.lessons.flatMap((l) => l.stages.map((s) => s.key)));

  const problems: Problem[] = [];
  for (const lesson of published.lessons) {
    if (!draftLessons.has(lesson.key)) {
      problems.push({
        path: `lessons.${lesson.key}`,
        message: "Lektionsschlüssel aus veröffentlichter Fassung fehlt",
      });
    }
    for (const stage of lesson.stages) {
      if (!draftStages.has(stage.key)) {
        problems.push({
          path: `lessons.${lesson.key}.${stage.key}`,
          message: "Stufenschlüssel aus veröffentlichter Fassung fehlt",
        });
      }
    }
  }

  // Der Testschlüssel zählt mit. An ihm hängen die beiden Messungen eines
  // Kindes; verschwindet er, ist der Eingangswert nicht mehr dem Abschlusswert
  // zuzuordnen – und der Vergleich ist der ganze Zweck des Tests.
  if (published.assessment && draft.assessment?.key !== published.assessment.key) {
    problems.push({
      path: "assessment",
      message:
        "Testschlüssel aus veröffentlichter Fassung fehlt – die Messungen der Kinder hängen daran",
    });
  }

  return problems;
}

/**
 * Nur die FORM prüfen: ist das überhaupt ein Bundle?
 *
 * Für die Admin-App, die einen Entwurf laden muss, an dem noch gearbeitet wird.
 * Ein Entwurf mit einem Zeichen ohne Finger oder ganz ohne Lektion ist nicht
 * kaputt, sondern unfertig – seine Hinweise zeigt `validate` daneben an, und
 * genau dafür ist er ja offen.
 *
 * Das ist keine zweite Wahrheit neben `parseBundle`: dieses baut auf jenem auf.
 * Die eine Prüfung ist die Form, die andere Form UND Inhalt.
 */
export function parseShape(raw: unknown): { bundle: Bundle } | { problems: Problem[] } {
  const parsed = bundleSchema.safeParse(raw);
  if (parsed.success) return { bundle: parsed.data };
  return {
    problems: parsed.error.issues.map((i) => ({
      path: i.path.join("."),
      message: i.message,
    })),
  };
}

/**
 * Form UND Inhalt – was die Lern-App braucht, bevor sie eine Fassung übernimmt.
 *
 * Sie fällt bei Problemen auf die eingebackene Fassung zurück; ein halb gültiges
 * Bundle wäre dort schlimmer als ein veraltetes.
 */
export function parseBundle(raw: unknown): { bundle: Bundle } | { problems: Problem[] } {
  const shape = parseShape(raw);
  if ("problems" in shape) return shape;
  const problems = checkBundle(shape.bundle);
  return problems.length > 0 ? { problems } : { bundle: shape.bundle };
}
